import { useCallback, useEffect, useRef, useState } from 'react';
import { ActionButton } from '@/components/assistant-ui/action-button';
import { publicValue, visibleFields } from '@/components/assistant-ui/action-field-privacy';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import agentPermissionsCopy from '@/i18n/ns/agent-permissions';
import agentRequestsCopy, { type AgentRequestsCopy } from '@/i18n/ns/agent-requests';
import { CanvasFooter } from '../canvas-footer';
import { useWorkspace } from '../context';
import { useInbox, type InboxItem } from '../inbox-api';
import { useInboxKey } from '../inbox';
import { LoadingState } from '../primitives';
import {
  agentRequestApi,
  requestErrorCode,
  type AgentRequestAction,
  type AgentRequestHeld,
} from './api';
import { familyLabel, nativeActionLabel } from './meta';

// What an Agent request says and asks (D2/D4 of PR2-DESIGN): the quoted call, where it
// would run, why it asks, and its own decision: Approve, Decline and, when the server
// offers it, "Don't ask again". The server decides every one of these; this only shows
// what it answered and sends the person's choice.

const MAX_VALUE = 1200;

// A value as text, without any credential, id or connection identity at any depth.
function valueText(value: unknown) {
  const visible = publicValue(value);
  const text = typeof visible === 'string' ? visible : (JSON.stringify(visible, null, 2) ?? '');
  return text.length > MAX_VALUE ? `${text.slice(0, MAX_VALUE)}…` : text;
}

// A connection call names its connection and tool around the input it sends: show only
// that input (where and what are said above it), with or without the server's connection
// details.
function callInput(action: AgentRequestAction): Record<string, unknown> {
  const args = (action.args ?? {}) as Record<string, unknown>;
  const inner = args.arguments;
  const wrapped = Boolean(action.decision?.connection) || 'connectionId' in args || 'tool' in args;
  return wrapped && inner && typeof inner === 'object' && !Array.isArray(inner)
    ? (inner as Record<string, unknown>)
    : args;
}

// An input field's name in words when the tool gives it no title ("channel_id" → "Channel id").
export const fieldName = (key: string) => {
  const words = key.split('_').join(' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

// The call's arguments, one line each, filtered as every other Chat approval card is; a call
// with a single text argument is just that text.
function argumentLines(action: AgentRequestAction) {
  const labels = action.decision?.connection?.fields ?? {};
  const entries = visibleFields(callInput(action), labels);
  return entries.map(([key, value]) => ({
    key,
    label: labels[key] || fieldName(key),
    text: valueText(value),
    labelled: entries.length > 1,
  }));
}

function whyAsks(copy: AgentRequestsCopy, action: AgentRequestAction, agent: string) {
  const held = action.outside?.held as AgentRequestHeld | undefined;
  if (held === 'floor') return formatCopy(copy.whyFloor, { agent });
  if (held === 'email_send') return formatCopy(copy.whyEmail, { agent });
  if (held === 'unknown_risk') return copy.whyUnknown;
  return formatCopy(
    action.outside?.family === 'data:connections' ? copy.whyAskConnections : copy.whyAsk,
    { agent },
  );
}

export function AgentRequestDetail({
  item,
  onSettled,
}: {
  item: InboxItem;
  onSettled: () => void;
}) {
  const copy = useNamespaceCopy(agentRequestsCopy);
  const permissions = useNamespaceCopy(agentPermissionsCopy);
  const ws = useWorkspace();
  const { inbox } = useInbox(useInboxKey());
  const id = item.ref?.id ?? '';
  const [action, setAction] = useState<AgentRequestAction | null>(null);
  const [failed, setFailed] = useState(false);
  const [grant, setGrant] = useState<{ token: string; expiresAt: number } | null>(null);
  const digest = useRef('');
  const [dontAsk, setDontAsk] = useState(false);
  const [busy, setBusy] = useState(false);

  // The card token the server gives when it is told the request was shown. It lives a few
  // minutes, so it is asked for again before it runs out and when the server says it is spent.
  const present = useCallback(
    async (forDigest: string) => {
      const shown = await agentRequestApi.presented(id, forDigest);
      const expires = Date.parse(shown.cardTokenExpiresAt ?? '');
      const next = shown.cardToken
        ? {
            token: shown.cardToken,
            expiresAt: Number.isFinite(expires) ? expires : Date.now() + 240_000,
          }
        : null;
      setGrant(next);
      return next;
    },
    [id],
  );

  // Read the request, then tell the server it was shown: that is what gives its decision a card token.
  useEffect(() => {
    let current = true;
    setAction(null);
    setFailed(false);
    setGrant(null);
    setDontAsk(false);
    (async () => {
      try {
        const read = await agentRequestApi.get(id);
        if (!current) return;
        digest.current = read.digest;
        setAction(read);
        if (read.status === 'pending' && read.canDecide) {
          await present(read.digest);
        }
      } catch {
        if (current) setFailed(true);
      }
    })();
    return () => {
      current = false;
    };
  }, [id, present]);

  // Renew before the token runs out; a failed renewal drops the token, and Approve then presents again when pressed.
  useEffect(() => {
    if (!grant) return undefined;
    const timer = setTimeout(
      () => {
        present(digest.current).catch(() => setGrant(null));
      },
      Math.max(1000, grant.expiresAt - Date.now() - 15_000),
    );
    return () => clearTimeout(timer);
  }, [grant, present]);

  if (failed) return <p className="ws-error">{copy.loadFailed}</p>;
  if (!action) return <LoadingState label={copy.loading} />;

  const agent = action.outside?.agentName || item.ref?.agentName || copy.unnamedAgent;
  const connection = action.decision?.connection;
  const grantId = action.outside?.grantId ?? item.ref?.grantId;
  const where = connection?.name || familyLabel(copy, action.outside?.family ?? item.ref?.family);
  const lines = argumentLines(action);
  const offered = action.outside?.allowAgain?.offered === true;
  const decidable = action.status === 'pending' && action.canDecide;
  const others = (inbox?.needsYou ?? [])
    .filter(
      (other) =>
        other.kind === 'agent_request' &&
        other.id !== item.id &&
        other.ref?.grantId === item.ref?.grantId,
    )
    .slice(0, 3);

  async function settle(done: string, tone?: 'error') {
    ws.notify(done, null, tone);
    onSettled();
    ws.openCanvas(null, { replace: true });
  }
  async function decide(kind: 'approve' | 'decline') {
    if (!action || busy) return;
    setBusy(true);
    try {
      if (kind === 'approve') {
        const send = (token: string) =>
          agentRequestApi.approve(id, {
            digest: action.digest,
            cardToken: token,
            dontAskAgain: offered && dontAsk,
          });
        // A token that has run out is asked for again first; one the server calls spent is
        // asked for again and the approval sent once more.
        const live = grant && grant.expiresAt > Date.now() ? grant : await present(action.digest);
        if (!live) throw new Error('not_presented');
        try {
          await send(live.token);
        } catch (failure) {
          if (requestErrorCode(failure) !== 'action_card_token_invalid') throw failure;
          const renewed = await present(action.digest);
          if (!renewed) throw failure;
          await send(renewed.token);
        }
        await settle(formatCopy(copy.approved, { agent }));
      } else {
        await agentRequestApi.reject(id, action.digest);
        await settle(copy.declined);
      }
    } catch (failure) {
      const code = requestErrorCode(failure);
      if (code === 'agent_request_void') {
        await settle(copy.requestVoid, 'error');
      } else if (code === 'allow_again_unavailable' || code === 'allowed_actions_limit') {
        // The remembered choice is not offered any more (or would pass the cap); the server rolled
        // the approval back and the request itself is still open.
        setDontAsk(false);
        setAction({
          ...action,
          outside: { ...action.outside, allowAgain: { offered: false, label: null } },
        });
        ws.notify(copy.allowAgainUnavailable, null, 'error');
      } else {
        ws.notify(copy.decisionFailed, null, 'error');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="ws-inbox-document">
        <div className="ws-markdown">
          {lines.length > 0 && (
            <blockquote>
              {lines.map((line) => (
                <p key={line.key} style={{ whiteSpace: 'pre-wrap' }}>
                  {line.labelled && <span className="ws-agent-request-field">{line.label}: </span>}
                  {line.text}
                </p>
              ))}
            </blockquote>
          )}
          <p>
            <strong>{copy.where}</strong> {formatCopy(copy.whereAsYou, { where })}
            {connection && connection.isRead !== true && <> {copy.connectionWrite}</>}
          </p>
          <p>
            <strong>{copy.whyAsks}</strong> {whyAsks(copy, action, agent)}{' '}
            <a
              href={
                grantId
                  ? `#/workspace/connections?agent=${encodeURIComponent(grantId)}`
                  : '#/workspace/connections'
              }
              onClick={(event) => {
                // With an agent the link itself opens its row in Connections.
                if (grantId) return;
                event.preventDefault();
                ws.navigate('connections');
              }}
            >
              {copy.seeWhatItMayDo}
            </a>
          </p>
          {others.length > 0 && (
            <p>
              <strong>{copy.othersLikeThis}</strong>{' '}
              {others.map((other, index) => (
                <span key={other.id}>
                  {index > 0 && ', '}
                  <a
                    href={`#/workspace/inbox?canvas=inbox&id=${encodeURIComponent(other.id)}`}
                    onClick={(event) => {
                      event.preventDefault();
                      ws.openCanvas({ kind: 'inbox', id: other.id });
                    }}
                  >
                    {other.title}
                  </a>
                </span>
              ))}
            </p>
          )}
        </div>
      </div>
      {!decidable && <p className="ws-agent-request-note">{copy.alreadyDecided}</p>}
      {decidable && (
        <CanvasFooter className="ws-inbox-canvas-footer ws-agent-request-footer">
          {offered ? (
            <label className="ws-agent-check ws-allow-again">
              <input
                type="checkbox"
                checked={dontAsk}
                disabled={busy}
                onChange={(event) => setDontAsk(event.target.checked)}
              />
              <span>
                <strong>
                  {formatCopy(copy.allowAgain, {
                    action:
                      nativeActionLabel(permissions, action.outside?.key) ??
                      action.outside?.allowAgain?.label ??
                      action.label,
                  })}
                </strong>
                <small>{formatCopy(copy.allowAgainDetail, { agent })}</small>
              </span>
            </label>
          ) : (
            <span />
          )}
          <div className="ws-actions">
            <ActionButton
              variant="secondary"
              data-size="md"
              disabled={busy}
              onClick={() => void decide('decline')}
            >
              {copy.decline}
            </ActionButton>
            <ActionButton
              variant="primary"
              data-size="md"
              busy={busy}
              disabled={busy}
              onClick={() => void decide('approve')}
            >
              {copy.approve}
            </ActionButton>
          </div>
        </CanvasFooter>
      )}
    </>
  );
}
